import { Directive, ElementRef, EventEmitter, forwardRef, HostBinding, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { deepCopy, isNullOrUndefined } from '@sla-ng/core';

@Directive({
    // eslint-disable-next-line @angular-eslint/directive-selector
    selector: '[inputMask]',
    providers: [ { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => MaskDirective), multi: true } ],
})
export class MaskDirective implements OnInit, OnChanges, ControlValueAccessor {

    @HostBinding('class.ui-inputwrapper-filled') public filled: boolean;
    @HostBinding('class.ui-inputwrapper-focus') public focus: boolean;

    @Input() public type: string = 'text';
    @Input() public slotChar: string = ' ';
    @Input() public autoClear: boolean = true;
    @Input() public style: string;
    @Input() public inputId: string;
    @Input() public styleClass: string;
    @Input() public placeholder: string;
    @Input() public size: number;
    @Input() public maxlength: number;
    @Input() public tabindex: string;
    @Input() public disabled: boolean;
    @Input() public readonly: boolean;
    @Input() public unmask: boolean;
    @Input() public name: string;
    @Input() public required: boolean;
    @Input() public defs: Record<string, RegExp>;
    @Input() public autoFocus: boolean;

    @Output() public onComplete: EventEmitter<unknown> = new EventEmitter();
    @Output() public onFocus: EventEmitter<unknown> = new EventEmitter();
    @Output() public onBlur: EventEmitter<unknown> = new EventEmitter();
    @Output() public onEnter: EventEmitter<unknown> = new EventEmitter();

    private value: string;
    private _mask: string;
    private input: HTMLInputElement;
    private tests: RegExp[];
    private partialPosition: number;
    private firstNonMaskPos: number;
    private lastRequiredNonMaskPos: number;
    private len: number;
    private oldVal: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private buffer: any;
    private defaultBuffer: string;
    private focusText: string;
    private caretTimeoutId: NodeJS.Timeout;
    private androidChrome: boolean;

    private oldEvent: unknown = null;

    constructor(public el: ElementRef) {
    }

    @HostListener('input', [ '$event' ])
    public onHostInput($event: unknown): void {
        if (this.oldEvent !== $event) {
            this.onInput($event);
        }
        this.oldEvent = $event;
    }

    @HostListener('paste', [ '$event' ])
    public onHostPaste($event: unknown): void {
        this.handleInputChange($event);
    }

    @HostListener('keyup.enter', [ '$event' ])
    public onHostEnter($event: KeyboardEvent): void {
        this.onInputEnter($event);
    }

    @HostListener('keydown', [ '$event' ])
    public onHostKeydown($event: KeyboardEvent): void {
        this.onKeyDown($event);
    }

    @HostListener('keypress', [ '$event' ])
    public onHostKeypress($event: KeyboardEvent): void {
        this.onKeyPress($event);
    }

    @HostListener('focus', [ '$event' ])
    public onHostFocus($event: unknown): void {
        this.onInputFocus($event);
    }

    @HostListener('blur', [ '$event' ])
    public onHostBlur($event: unknown): void {
        this.onInputBlur($event);
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    @Input()
    public get inputMask(): string {
        return this._mask;
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public set inputMask(val: string) {
        this._mask = val;

        this.initMask();
        this.writeValue('');
        this.onModelChange(this.value);
    }

    public ngOnInit(): void {
        const ua: string = navigator.userAgent;
        this.androidChrome = /chrome/i.test(ua) && /android/i.test(ua);
        this.initMask();
    }

    public ngOnChanges(_changes: SimpleChanges): void {
        const element: HTMLInputElement | HTMLTextAreaElement = this.getInputElement();
        if (element) {
            isNullOrUndefined(this.autoFocus) ? element.removeAttribute('autofocus') : element.setAttribute('autofocus', `${this.autoFocus}`);
            isNullOrUndefined(this.inputId) ? element.removeAttribute('inputId') : element.setAttribute('id', `${this.inputId}`);
            isNullOrUndefined(this.type) ? element.removeAttribute('type') : element.setAttribute('type', `${this.type}`);
            isNullOrUndefined(this.name) ? element.removeAttribute('name') : element.setAttribute('name', `${this.name}`);
            isNullOrUndefined(this.placeholder) ? element.removeAttribute('placeholder') : element.setAttribute('placeholder', `${this.placeholder}`);
            isNullOrUndefined(this.size) ? element.removeAttribute('size') : element.setAttribute('size', `${this.size}`);
            isNullOrUndefined(this.maxlength) ? element.removeAttribute('maxlength') : element.setAttribute('maxlength', `${this.maxlength}`);
            isNullOrUndefined(this.tabindex) ? element.removeAttribute('tabindex') : element.setAttribute('tabindex', `${this.tabindex}`);
            isNullOrUndefined(this.disabled) ? element.removeAttribute('disabled') : element.setAttribute('disabled', `${this.disabled}`);
            isNullOrUndefined(this.readonly) ? element.removeAttribute('readonly') : element.setAttribute('readonly', `${this.readonly}`);
            isNullOrUndefined(this.required) ? element.removeAttribute('required') : element.setAttribute('required', `${this.required}`);
        }
    }

    // eslint-disable-next-line @typescript-eslint/ban-types
    public onModelChange: Function = () => {/**/
    };
    // eslint-disable-next-line @typescript-eslint/ban-types
    public onModelTouched: Function = () => {/**/
    };

    public writeValue(value: string): void {
        this.value = value;
        const element: HTMLInputElement | HTMLTextAreaElement = this.getInputElement();
        if (element) {
            if (this.value === undefined) {
                element.value = '';
            } else {
                element.value = this.value;
            }

            this.checkVal(true);
            this.focusText = element.value;
            this.updateFilledState();
        }
    }

    // eslint-disable-next-line @typescript-eslint/ban-types
    public registerOnChange(fn: Function): void {
        this.onModelChange = fn;
    }

    // eslint-disable-next-line @typescript-eslint/ban-types
    public registerOnTouched(fn: Function): void {
        this.onModelTouched = fn;
    }

    public setDisabledState(val: boolean): void {
        this.disabled = val;
    }

    private caret(first?: number, last?: number): { begin: number, end: number } {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let range: any;
        let begin: number;
        let end: number;

        const element: HTMLInputElement | HTMLTextAreaElement = this.getInputElement();
        if (!element.offsetParent || element !== document.activeElement) {
            return;
        }

        if (typeof first === 'number') {
            begin = first;
            end = (typeof last === 'number') ? last : begin;
            if (element.setSelectionRange) {
                element.setSelectionRange(begin, end);
            } else if (element[ 'createTextRange' ]) {
                range = element[ 'createTextRange' ]();
                range.collapse(true);
                range.moveEnd('character', end);
                range.moveStart('character', begin);
                range.select();
            }
        } else {
            if (element.setSelectionRange) {
                begin = element.selectionStart;
                end = element.selectionEnd;
            } else if (document[ 'selection' ]?.createRange) {
                range = document[ 'selection' ].createRange();
                begin = 0 - range.duplicate().moveStart('character', -100000);
                end = begin + range.text.length;
            }

            return { begin, end };
        }
    }

    private isCompleted(): boolean {
        for (let i: number = this.firstNonMaskPos; i <= this.lastRequiredNonMaskPos; i++) {
            if (this.tests[ i ] && this.buffer[ i ] === this.getPlaceholder(i)) {
                return false;
            }
        }
        return true;
    }

    private getPlaceholder(i: number): string {
        if (i < (this.slotChar || ' ').length) {
            return (this.slotChar || ' ').charAt(i);
        }
        return (this.slotChar || ' ').charAt(0);
    }

    private seekNext(pos: number): number {
        while (++pos < this.len && !this.tests[ pos ]) {/**/}
        return pos;
    }

    private seekPrev(pos: number): number {
        while (--pos >= 0 && !this.tests[ pos ]) {/**/}
        return pos;
    }

    private shiftL(begin: number, end: number): void {
        let i: number;
        let j: number;

        if (begin < 0) {
            return;
        }

        for (i = begin, j = this.seekNext(end); i < this.len; i++) {
            if (this.tests[ i ]) {
                if (j < this.len && this.tests[ i ].test(this.buffer[ j ])) {
                    this.buffer[ i ] = this.buffer[ j ];
                    this.buffer[ j ] = this.getPlaceholder(j);
                } else {
                    break;
                }

                j = this.seekNext(j);
            }
        }
        this.writeBuffer();
        this.caret(Math.max(this.firstNonMaskPos, begin));
    }

    private shiftR(pos: number): void {
        let i: number;
        let c: string;
        let j: number;
        let t: string;

        for (i = pos, c = this.getPlaceholder(pos); i < this.len; i++) {
            if (this.tests[ i ]) {
                j = this.seekNext(i);
                t = this.buffer[ i ];
                this.buffer[ i ] = c;
                if (j < this.len && this.tests[ j ].test(t)) {
                    c = t;
                } else {
                    break;
                }
            }
        }
    }

    private handleAndroidInput(e: unknown): void {
        const element: HTMLInputElement | HTMLTextAreaElement = this.getInputElement();
        const curVal: string = element.value;
        const pos: { begin: number, end: number } = this.caret();
        if (this.oldVal?.length && this.oldVal.length > curVal.length) {
            // a deletion or backspace happened
            this.checkVal(true);
            while (pos.begin > 0 && !this.tests[ pos.begin - 1 ]) {
                pos.begin--;
            }
            if (pos.begin === 0) {
                while (pos.begin < this.firstNonMaskPos && !this.tests[ pos.begin ]) {
                    pos.begin++;
                }
            }

            setTimeout(() => {
                this.caret(pos.begin);
                this.updateModel(e);
                if (this.isCompleted()) {
                    this.onComplete.emit();
                }
            }, 0);
        } else {
            this.checkVal(true);
            while (pos.begin < this.len && !this.tests[ pos.begin ]) {
                pos.begin++;
            }
            setTimeout(() => {
                this.caret(pos.begin);
                this.updateModel(e);
                if (this.isCompleted()) {
                    this.onComplete.emit();
                }
            }, 0);
        }
    }

    private onInputBlur(e: unknown): void {
        const element: HTMLInputElement | HTMLTextAreaElement = this.getInputElement();

        this.focus = false;
        this.onModelTouched();
        this.checkVal(true);
        // this.updateModel(e);
        this.updateFilledState();
        this.onBlur.emit(e);
        if (element.value !== this.focusText) {
            const event: Event = document.createEvent('HTMLEvents');
            event.initEvent('change', true, false);
            element.dispatchEvent(event);
        }
    }

    private onInputEnter(e: unknown): void {
        this.onEnter.emit(e);
    }

    private onKeyDown(e: KeyboardEvent): void {
        const element: HTMLInputElement | HTMLTextAreaElement = this.getInputElement();

        if (this.readonly) {
            return;
        }

        const k: number = e.which || e.keyCode;

        let pos: { begin: number, end: number };
        let begin: number;
        let end: number;
        const iPhone: boolean = /iphone/i.test(navigator.userAgent);
        this.oldVal = element.value;

        // backspace, delete, and escape get special treatment
        if (k === 8 || k === 46 || (iPhone && k === 127)) {
            pos = this.caret();
            begin = pos.begin;
            end = pos.end;

            if (end - begin === 0) {
                begin = k !== 46 ? this.seekPrev(begin) : (end = this.seekNext(begin - 1));
                end = k === 46 ? this.seekNext(end) : end;
            }

            this.clearBuffer(begin, end);
            this.shiftL(begin, end - 1);
            this.updateModel(e);

            e.preventDefault();
        } else if (k === 13) { // enter
            this.onInputBlur(e);
            this.updateModel(e);
        } else if (k === 27) { // escape
            element.value = this.focusText;
            this.caret(0, this.checkVal());
            this.updateModel(e);
            e.preventDefault();
        }
    }

    private onKeyPress(e: KeyboardEvent): void {
        if (this.readonly) {
            return;
        }

        const k: number = e.which || e.keyCode;
        const pos: { begin: number, end: number } = this.caret();
        let p: number;
        let c: string;
        let next: number;
        let completed: boolean;

        if (e.ctrlKey || e.altKey || e.metaKey || k < 32 || (k > 34 && k < 41)) {// Ignore
            return;
        } else if (k && k !== 13) {
            if (pos.end - pos.begin !== 0) {
                this.clearBuffer(pos.begin, pos.end);
                this.shiftL(pos.begin, pos.end - 1);
            }

            p = this.seekNext(pos.begin - 1);
            if (p < this.len) {
                c = String.fromCharCode(k);
                if (this.tests[ p ].test(c)) {
                    this.shiftR(p);

                    this.buffer[ p ] = c;
                    this.writeBuffer();
                    next = this.seekNext(p);

                    if (/android/i.test(navigator.userAgent)) {
                        // Path for CSP Violation on FireFox OS 1.1
                        setTimeout(() => {
                            this.caret(next);
                        }, 0);
                    } else {
                        this.caret(next);
                    }
                    if (pos.begin <= this.lastRequiredNonMaskPos) {
                        completed = this.isCompleted();
                    }
                }
            }
            e.preventDefault();
        }

        this.updateModel(e);

        this.updateFilledState();

        if (completed) {
            this.onComplete.emit();
        }
    }

    private clearBuffer(start: number, end: number): void {
        let i: number;
        for (i = start; i < end && i < this.len; i++) {
            if (this.tests[ i ]) {
                this.buffer[ i ] = this.getPlaceholder(i);
            }
        }
    }

    private writeBuffer(): void {
        const element: HTMLInputElement | HTMLTextAreaElement = this.getInputElement();

        // const index: number = this.buffer.findIndex((char: string) => char.length === 0);
        // const buffer: string = index > -1 ? this.buffer.slice(0, index) : this.buffer;
        // element.value = buffer.join('');
        element.value = this.getMaskValue();
    }

    private checkVal(allow?: boolean): number {
        const element: HTMLInputElement | HTMLTextAreaElement = this.getInputElement();
        // try to place characters where they belong
        const test: string = element.value;
        let lastMatch: number = -1;
        let i: number;
        let c: string;
        let pos: number;

        for (i = 0, pos = 0; i < this.len; i++) {
            if (this.tests[ i ]) {
                this.buffer[ i ] = this.getPlaceholder(i);
                while (pos++ < test.length) {
                    c = test.charAt(pos - 1);
                    if (this.tests[ i ].test(c)) {
                        this.buffer[ i ] = c;
                        lastMatch = i;
                        break;
                    }
                }
                if (pos > test.length) {
                    this.clearBuffer(i + 1, this.len);
                    break;
                }
            } else {
                if (this.buffer[ i ] === test.charAt(pos)) {
                    pos++;
                }
                if (i < this.partialPosition) {
                    lastMatch = i;
                }
            }
        }
        if (allow) {
            this.writeBuffer();
        } else if (lastMatch + 1 < this.partialPosition) {
            if (this.autoClear || this.buffer.join('') === this.defaultBuffer) {
                // Invalid value. Remove it and replace it with the
                // mask, which is the default behavior.
                if (element.value) {
                    element.value = '';
                }
                this.clearBuffer(0, this.len);
            } else {
                // Invalid value, but we opt to show the value to the
                // user and allow them to correct their mistake.
                this.writeBuffer();
            }
        } else {
            this.writeBuffer();
            element.value = element.value.substring(0, lastMatch + 1);
        }
        return (this.partialPosition ? i : this.firstNonMaskPos);
    }

    private onInputFocus(event: unknown): void {
        const element: HTMLInputElement | HTMLTextAreaElement = this.getInputElement();

        if (this.readonly) {
            return;
        }

        this.focus = true;

        clearTimeout(this.caretTimeoutId);

        this.focusText = element.value;

        const pos: number = this.checkVal(true);

        this.caretTimeoutId = setTimeout(() => {
            if (element !== document.activeElement) {
                return;
            }
            this.writeBuffer();
            if (pos === this.inputMask.replace('?', '').length) {
                this.caret(0, pos);
            } else {
                this.caret(pos);
            }
        }, 10);

        this.onFocus.emit(event);
    }

    private onInput(event: unknown): void {
        if (this.androidChrome) {
            this.handleAndroidInput(event);
        } else {
            this.handleInputChange(event);
        }
    }

    private handleInputChange(event: unknown): void {
        if (this.readonly) {
            return;
        }

        setTimeout(() => {
            const pos: number = this.checkVal(true);
            this.caret(pos);
            this.updateModel(event);
            if (this.isCompleted()) {
                this.onComplete.emit();
            }
        }, 0);
    }

    private getMaskValue(): string {
        const unmaskedBuffer: string[] = [];
        const buffer: string[] = deepCopy(this.buffer) as string[];
        buffer.reverse();
        const index: number = buffer.length - buffer.findIndex((val: string, i: number) => val !== this.getPlaceholder(buffer.length - i));
        for (let i: number = 0; i < index; i++) {
            unmaskedBuffer.push(this.buffer[ i ]);
        }
        for (let i: number = index; i < buffer.length; i++) {
            const c: string = this.buffer[ i ];
            if (!this.tests[ i ] && c === this.getPlaceholder(i)) {
                unmaskedBuffer.push(c);
            } else {
                break;
            }
        }
        if (unmaskedBuffer.find((val: string) => val !== (this.slotChar || ' '))) {
            return unmaskedBuffer.join('');
        } else {
            return '';
        }
    }

    private getUnmaskedValue(): string {
        const unmaskedBuffer: string[] = [];
        for (let i: number = 0; i < this.buffer.length; i++) {
            const c: string = this.buffer[ i ];
            if (this.tests[ i ] && c !== this.getPlaceholder(i)) {
                unmaskedBuffer.push(c);
            }
        }

        return unmaskedBuffer.join('');
    }

    private updateModel(_e: unknown): void {
        const updatedValue: string = this.unmask ? this.getUnmaskedValue() : this.getInputElement().value;
        if (updatedValue !== this.value && (updatedValue || this.value !== null)) {
            const event: Event = new Event('input', {
                bubbles: true,
                cancelable: true,
            });
            this.getInputElement().dispatchEvent(event);
            this.value = updatedValue || null;
            this.onModelChange(this.value);
        }
    }

    private updateFilledState(): void {
        const element: HTMLInputElement | HTMLTextAreaElement = this.getInputElement();
        this.filled = element && element.value !== '';
    }

    private initMask(): void {
        this.tests = [];
        this.partialPosition = this.inputMask.length;
        this.len = this.inputMask.length;
        this.firstNonMaskPos = null;
        if (!this.defs) {
            this.defs = {
                '´': /[A-Za-z]/,
                '`': /[0-9]/,
                '*': /./,
            };
        }

        const maskTokens: string[] = this.inputMask.split('');

        for (let i: number = 0; i < maskTokens.length; i++) {
            const c: string = maskTokens[ i ];
            if (c === '?') {
                this.len--;
                this.partialPosition = i;
            } else if (this.defs[ c ]) {
                this.tests.push(this.defs[ c ]);
                if (this.firstNonMaskPos === null) {
                    this.firstNonMaskPos = this.tests.length - 1;
                }
                if (i < this.partialPosition) {
                    this.lastRequiredNonMaskPos = this.tests.length - 1;
                }
            } else {
                this.tests.push(null);
            }
        }

        this.buffer = [];
        for (let i: number = 0; i < maskTokens.length; i++) {
            const c: string = maskTokens[ i ];
            if (c !== '?') {
                if (this.defs[ c ]) {
                    this.buffer.push(this.getPlaceholder(i));
                } else {
                    this.buffer.push(c);
                }
            }
        }
        this.defaultBuffer = this.buffer.join('');
    }

    private getInputElement(): HTMLInputElement | HTMLTextAreaElement {
        let element: HTMLElement = this.el.nativeElement;
        if (!(element instanceof HTMLInputElement) && !(element instanceof HTMLTextAreaElement)) {
            element = element.querySelector('input') || element.querySelector('textarea');
        }
        if (!element) {
            console.warn('Input equals null!');
        }
        return element as HTMLTextAreaElement | HTMLInputElement;
    }
}
